import React, { useState, useEffect, useContext } from 'react';
import PortalHeader from "../components/PortalHeader.js";
import '../styles/portal.css';
import CustomTextField from '../utils/custom_textfield.js';
import OtpInput from '../utils/otp_input.js';
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { AuthContext } from "../context/AuthContext.js";
import { DataContext } from "../context/DataContext.js";
import ApplicationCard from '../components/ApplicationCard.js';
import { LoadingComponent } from '../components/LoadingComponent.js';
import Alert from '../components/Alert.js';
import { buildUrlWithUTM, extractUTMParameters, storeUTMParameters, getStoredUTMParameters } from '../utils/utmUtils.js';


const PortalDashboardPage = () => {
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const dataContext = useContext(AuthContext);
  const { getApplicationData, applicationData, dataLoading } = useContext(DataContext);  // Correctly access getApplicationData from DataContext
  const { authLoading, authError, setAuthError } = useContext(AuthContext);  // Correctly access getApplicationData from DataContext
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  // Capture UTM parameters on component mount
  useEffect(() => {
    const utmParams = extractUTMParameters();
    if (utmParams.utm_source || utmParams.utm_medium || utmParams.utm_campaign) {
      storeUTMParameters(utmParams);
      console.log('UTM Parameters captured on dashboard:', utmParams);
    } else {
      // Clear stored UTM parameters if current URL doesn't have them
      localStorage.removeItem('utm_parameters');
    }
  }, []);

  const handleLogout = () => {
    // Preserve UTM parameters before logout clears localStorage
    // Use both window.location and React Router location for reliability
    const urlParamsFromWindow = new URLSearchParams(window.location.search);
    const urlParamsFromRouter = new URLSearchParams(location.search);
    
    const currentUtmParams = {
      utm_source: urlParamsFromRouter.get('utm_source') || urlParamsFromWindow.get('utm_source') || '',
      utm_medium: urlParamsFromRouter.get('utm_medium') || urlParamsFromWindow.get('utm_medium') || '',
      utm_campaign: urlParamsFromRouter.get('utm_campaign') || urlParamsFromWindow.get('utm_campaign') || ''
    };
    
    const storedUtmParams = getStoredUTMParameters();
    const utmParams = (currentUtmParams.utm_source || currentUtmParams.utm_medium || currentUtmParams.utm_campaign) 
      ? currentUtmParams 
      : storedUtmParams;
    
    console.log('Logout - Current URL:', window.location.href);
    console.log('Logout - Router location search:', location.search);
    console.log('Logout - Current UTM params:', currentUtmParams);
    console.log('Logout - Stored UTM params:', storedUtmParams);
    console.log('Logout - Using UTM params:', utmParams);
    
    authContext.logout();
    console.log("Logout successfully");
    
    // Build URL with UTM parameters directly
    let loginUrl = '/portal/login';
    if (utmParams.utm_source || utmParams.utm_medium || utmParams.utm_campaign) {
      const params = new URLSearchParams();
      if (utmParams.utm_source) params.append('utm_source', utmParams.utm_source);
      if (utmParams.utm_medium) params.append('utm_medium', utmParams.utm_medium);
      if (utmParams.utm_campaign) params.append('utm_campaign', utmParams.utm_campaign);
      loginUrl = `${loginUrl}?${params.toString()}`;
      
      // Restore UTM parameters to localStorage after logout so they persist on login page
      storeUTMParameters(utmParams);
    }
    
    console.log('Logout - Navigating to:', loginUrl);
    // Use window.location.href to ensure URL is updated in browser
    window.location.href = loginUrl;
  }
  const handleResume = () => {
    navigate(buildUrlWithUTM('/portal/application-form'));
  }
  const handlePaynow = () => {
    navigate(buildUrlWithUTM('/portal/payment-options'));
  };


  useEffect(() => {
    if (authError) {
      setError(authError);
    }
    setAuthError(null);
  }, [authError]);

  useEffect(() => {
    if (!authContext.loading) {
      if (!authContext.isAuthenticated) {
        navigate(buildUrlWithUTM("/portal/login"));
      }
    }
  }, [authContext.isAuthenticated, authContext.loading]);

  useEffect(() => {
    console.log("State Sent ", location.state);
    if (location.state?.fromRegister) {
      setError({ "type": "success", "message": "Register Successfully" });
          window.history.replaceState({}, document.title);
    }
    if (location.state?.fromLogin) {
      setError({ "type": "success", "message": "Login Successfully" });
          window.history.replaceState({}, document.title);
    }
    // After Easebuzz redirect: show message and refetch so Fee Paid / Fee Remaining (image 3) are updated
    const params = new URLSearchParams(location.search);
    if (params.get('payment_success') === '1') {
      const amount = params.get('amount') || '';
      setError({ "type": "success", "message": `Payment of Rs ${amount} received. Fee status updated.` });
      window.history.replaceState({}, document.title, location.pathname);
    }
    if (params.get('payment_failure') === '1') {
      setError({ "type": "error", "message": params.get('error') || "Payment failed or was cancelled." });
      window.history.replaceState({}, document.title, location.pathname);
    }
    getApplicationData();
  }, []);

  useEffect(() => {
    console.log("Application Data dash:", applicationData);
    // Store application_id in localStorage for payment flow
    if (applicationData && applicationData.application_id) {
      localStorage.setItem("application_id", applicationData.application_id);
    }
  }, [applicationData]);

  return (
    <>
      <PortalHeader >
        <nav className="nav-links">
          <a href={buildUrlWithUTM("/portal")} className="nav-link" onClick={(e) => { e.preventDefault(); navigate(buildUrlWithUTM('/portal')); }}>Home</a>

          {/* <a href="/portal/dashboard" className="nav-link">Dashboard</a> */}
        </nav>
        <button className="logout-button" onClick={handleLogout}>Logout</button>
      </PortalHeader>

      {error && <Alert error={error} onClose={() => setError(null)} />}

      <div className='application-cards'>
        {dataLoading || authLoading ? <LoadingComponent /> :
          applicationData ?
            <ApplicationCard
              appNumber={applicationData ? applicationData.application_id : "Loading"}
              candidateName={applicationData ? applicationData.name : "Deepanshu Kaushik"}
              program={applicationData ? applicationData.program : "AURUM Banker Program"}
              status={applicationData ? applicationData.status : ""}
              paymentStatus={applicationData && applicationData.razorpay_payment_status === 'captured' ? 'Paid' : 'Unpaid'}
              paymentAmount={applicationData ? applicationData.razorpay_payment_amount : 0}
              onResume={handleResume}
              onPaynow={handlePaynow}
              totalPaidAmount={applicationData ? applicationData.total_paid_amount : 0}
              programTotalFee={applicationData ? applicationData.program_total_fee : 236000}
              paymentCompleted={applicationData ? applicationData.payment_completed : false}
              isSelectedInResult={applicationData ? applicationData.is_selected_in_result : false}
            /> : <ApplicationCard
              appNumber={applicationData ? applicationData.application_id : "NA"}
              candidateName={applicationData ? applicationData.name : "Not found"}
              program={applicationData ? applicationData.program : "AURUM Banker Program"}
              status={applicationData ? applicationData.status : "Error"}
              paymentStatus={'Unpaid'}
              paymentAmount={0}
              onResume={handleResume}
              onPaynow={handlePaynow}
              totalPaidAmount={0}
              programTotalFee={236000}
              paymentCompleted={false}
              isSelectedInResult={false}
            />
        }
      </div>
    </>
  );
}

export default PortalDashboardPage;
